import { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import intl from "react-intl-universal";
import { ModalCloseButton } from "../../../../../Components/Molecules/ValutDialog";
import { isBaasHmlInstance } from "../../../../../Services/baasInstancesApi";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const toggleTrackClass = (on) =>
  `relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition ${
    on ? "bg-[#e7a73f]" : "bg-gray-200 dark:bg-gray-700"
  }`;

const toggleThumbClass = (on) =>
  `pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
    on ? "translate-x-5" : "translate-x-0"
  }`;

const ToggleRow = ({ label, hint, checked, onChange, disabled }) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-3 py-2.5 dark:border-brand-dark-border">
    <div>
      <p className="text-sm font-medium text-brand-dark dark:text-gray-100">{label}</p>
      {hint ? <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p> : null}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`${toggleTrackClass(checked)} ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
    >
      <span className={toggleThumbClass(checked)} />
    </button>
  </div>
);

export const BaasEditInstanceModal = ({
  open,
  instance,
  onClose,
  onSubmit,
  submitting = false,
  canEditCatalogFlags = false,
}) => {
  const [instanceName, setInstanceName] = useState("");
  const [isProduction, setIsProduction] = useState(true);
  const isHmlLane = isBaasHmlInstance(instance);

  useEffect(() => {
    if (!open || !instance) {
      return;
    }

    setInstanceName(instance.displayName || instance.name || "");
    const env = String(instance.environment || "").toLowerCase();
    const prod =
      typeof instance.isProduction === "boolean"
        ? instance.isProduction
        : env === "production";
    // HML never liberates production — keep the switch off in the UI.
    setIsProduction(isBaasHmlInstance(instance) ? false : prod);
  }, [open, instance]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!instanceName.trim() || !instance) {
      return;
    }

    // HML cards do not PATCH is_production — liberation lives on the Prod twin.
    if (isBaasHmlInstance(instance)) {
      await onSubmit({
        name: instanceName.trim(),
      });
      onClose();
      return;
    }

    const wasProduction =
      typeof instance.isProduction === "boolean"
        ? instance.isProduction
        : String(instance.environment || "").toLowerCase() === "production";
    // Only request HML twin when promoting into production without an HML link.
    // MobileService intercepts createHmlTwin and creates the twin cross-DB in bank.
    const promotingToProd = isProduction && !wasProduction;
    const needsHmlTwin = promotingToProd && !instance?.hmlInstanceId;

    await onSubmit({
      name: instanceName.trim(),
      environment: isProduction ? "production" : "development",
      isProduction,
      ...(needsHmlTwin ? { createHmlTwin: true } : {}),
    });

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "!rounded-2xl !shadow-card" }}
    >
      <form onSubmit={handleSubmit} className="relative px-5 py-6 sm:px-6">
        <ModalCloseButton onClick={handleClose} className="absolute right-3 top-3" disabled={submitting} />
        <h2 className="pr-8 text-lg font-bold text-brand-dark dark:text-gray-100">
          {intl.get("baas.instances.editModal.title")}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.instances.editModal.subtitle")}
        </p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("baas.instances.editModal.nameLabel")}
            </label>
            <input
              className={inputClass}
              value={instanceName}
              onChange={(event) => setInstanceName(event.target.value)}
              placeholder={intl.get("baas.instances.editModal.namePlaceholder")}
              required
            />
          </div>

          {/* Liberation lives on the Prod catalog card only — never show on HML. */}
          {canEditCatalogFlags && !isHmlLane ? (
            <ToggleRow
              label={intl.get("baas.instances.createPage.isProdLabel")}
              hint={intl.get("baas.instances.createPage.isProdHintAdmin")}
              checked={isProduction}
              onChange={setIsProduction}
              disabled={submitting}
            />
          ) : null}
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting || !instanceName.trim()}
          >
            {intl.get("baas.instances.editModal.submit")}
          </button>
        </div>
      </form>
    </Dialog>
  );
};
